import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * React-free policy engine deciding *which* remote members we subscribe to
 * video from, given the set currently visible on screen. It exists so the app
 * subscribes only to visible tiles rather than to every participant, and so the
 * decision logic — debounce, rate limiting, flap cancellation — is plain,
 * synchronous and unit-testable in isolation from Redux and the DOM.
 *
 * The design mirrors {@link createSpeakingDetector}: no internal timers, no
 * wall-clock reads. Every method takes an explicit `nowMs`, so tests drive it by
 * advancing a number. Debounced actions that need to fire purely because time
 * passed (no new visibility change) are surfaced by calling {@link tick} on a
 * timer — the manager itself never schedules anything.
 *
 * Convergence rules, per member:
 * - Desired = "is in the visible set". When desired already matches the current
 *   subscription, the member is settled and nothing happens.
 * - On a disagreement (visible-but-not-subscribed, or subscribed-but-hidden) a
 *   pending run starts. The opposite action fires only once the disagreement has
 *   held continuously for the relevant delay — subscribeDelayMs going up,
 *   unsubscribeDelayMs coming down. If the member flaps back to agreement before
 *   then, the pending run is cancelled and no action is emitted. This is what
 *   stops sub/unsub spam.
 * - A rate limit (minResubscribeMs) additionally forbids acting on a member
 *   within that window of its last action; the pending run is held (not dropped)
 *   and retried on a later tick, so the action is delayed rather than lost.
 * - Self-healing: the caller reports peer liveness ({@link
 *   VideoSubscriptionManager.reportPeerLiveness}); a held subscription whose
 *   peer stays dead past peerLostGraceMs is demoted (no callback — the peer is
 *   already gone) so the rules above re-subscribe it if still visible. This is
 *   what recovers video after a signaling reconnect tears every receiver peer
 *   down while membership — and thus the visible set — never changes.
 */
export var createVideoSubscriptionManager = function createVideoSubscriptionManager(_ref) {
  var onSubscribe = _ref.onSubscribe,
    onUnsubscribe = _ref.onUnsubscribe,
    subscribeDelayMs = _ref.subscribeDelayMs,
    unsubscribeDelayMs = _ref.unsubscribeDelayMs,
    minResubscribeMs = _ref.minResubscribeMs,
    peerLostGraceMs = _ref.peerLostGraceMs,
    log = _ref.log;
  var emit = function emit(message) {
    if (log) {
      log(message);
    }
  };

  // Per-member bookkeeping. A member absent from the map is treated as
  // unsubscribed with no history — records are only created once a member has
  // been visible or acted upon, so the map stays bounded by meeting membership.
  var members = new Map();
  // The desired set: members that should currently be subscribed.
  var visible = new Set();
  var ensureMember = function ensureMember(name) {
    var state = members.get(name);
    if (!state) {
      state = {
        subscribed: false,
        pendingSince: null,
        lastAction: null,
        lastActionAt: null,
        peerSeenLive: false,
        peerLostSince: null
      };
      members.set(name, state);
    }
    return state;
  };

  // Reconcile a single member toward its desired state, emitting an action only
  // when both the debounce gate and the rate-limit gate allow it.
  var reconcileMember = function reconcileMember(name, state, nowMs) {
    var desired = visible.has(name);
    // Settled — clear any leftover pending run and stop.
    if (desired === state.subscribed) {
      state.pendingSince = null;
      return;
    }
    // A disagreeing member must have a start time; setVisibleMembers sets it,
    // but seed it here too so a bare tick still behaves correctly.
    if (state.pendingSince === null) {
      state.pendingSince = nowMs;
    }
    var debounceMs = desired ? subscribeDelayMs : unsubscribeDelayMs;
    if (nowMs - state.pendingSince < debounceMs) {
      return; // still within the debounce window
    }
    if (state.lastActionAt !== null && nowMs - state.lastActionAt < minResubscribeMs) {
      // Rate-limited: keep the pending run alive so a later tick retries
      // once the window has elapsed — the action is deferred, not dropped.
      return;
    }
    var heldMs = nowMs - state.pendingSince;
    state.subscribed = desired;
    state.pendingSince = null;
    state.lastAction = desired ? 'subscribe' : 'unsubscribe';
    state.lastActionAt = nowMs;
    // Loss-detection re-arms per action: the peer this action creates (or
    // destroys) hasn't been observed yet, so stale liveness from the
    // previous peer must not count for the new one.
    state.peerSeenLive = false;
    state.peerLostSince = null;
    if (desired) {
      emit("subscribe \"".concat(name, "\" (visible ").concat(heldMs, "ms before firing)"));
      onSubscribe(name);
    } else {
      emit("unsubscribe \"".concat(name, "\" (hidden ").concat(heldMs, "ms before firing)"));
      onUnsubscribe(name);
    }
  };
  var flush = function flush(nowMs) {
    members.forEach(function (state, name) {
      if (state.pendingSince !== null) {
        reconcileMember(name, state, nowMs);
      }
    });
  };
  return {
    setVisibleMembers: function setVisibleMembers(memberNames, nowMs) {
      visible = new Set(memberNames);
      // Track any newly-visible member so the reconcile loop considers it.
      visible.forEach(function (name) {
        return ensureMember(name);
      });
      // Start a pending run for every member whose desired state disagrees
      // with its subscription, and cancel it the instant they agree again
      // (the flap cancellation that prevents churn).
      members.forEach(function (state, name) {
        var desired = visible.has(name);
        if (desired === state.subscribed) {
          if (state.pendingSince !== null) {
            // The member reverted to agreement before its debounce
            // fired — the flap cancellation that prevents sub/unsub
            // spam. Log it so that non-churn is observable.
            emit("cancelled pending ".concat(state.subscribed ? 'unsubscribe' : 'subscribe', " for \"").concat(name, "\" (reverted before the debounce elapsed)"));
          }
          state.pendingSince = null;
        } else if (state.pendingSince === null) {
          state.pendingSince = nowMs;
        }
      });
      flush(nowMs);
    },
    tick: function tick(nowMs) {
      flush(nowMs);
    },
    reportPeerLiveness: function reportPeerLiveness(name, hasLivePeer, nowMs) {
      var state = members.get(name);
      // Liveness only means something for a subscription we believe we
      // hold — an unsubscribed member's peer is legitimately dead.
      if (!(state !== null && state !== void 0 && state.subscribed)) {
        return;
      }
      if (hasLivePeer) {
        state.peerSeenLive = true;
        state.peerLostSince = null;
        return;
      }
      // Dead but never seen live: our subscribe's peer setup is still in
      // flight — that's the setup path's business, not a loss.
      if (!state.peerSeenLive) {
        return;
      }
      if (state.peerLostSince === null) {
        state.peerLostSince = nowMs;
        return;
      }
      if (nowMs - state.peerLostSince < peerLostGraceMs) {
        return;
      }
      // The peer we held was torn down behind our back (e.g. a signaling
      // reconnect's leaveVideoRoom) and stayed dead past the grace. Demote
      // to unsubscribed WITHOUT a callback — there is nothing left to
      // close — and, if the member is still visible, start the pending run
      // here so the next flush re-subscribes even though no visibility
      // change will arrive to start it.
      var lostForMs = nowMs - state.peerLostSince;
      state.subscribed = false;
      state.peerSeenLive = false;
      state.peerLostSince = null;
      state.pendingSince = visible.has(name) ? nowMs : null;
      emit("peer for subscribed \"".concat(name, "\" gone for ").concat(lostForMs, "ms (external teardown, e.g. reconnect) \u2014 dropped the held subscription").concat(state.pendingSince !== null ? '; re-subscribing' : ''));
    },
    forgetMember: function forgetMember(name) {
      members["delete"](name);
      visible["delete"](name);
    },
    getSubscribedMembers: function getSubscribedMembers() {
      var result = [];
      members.forEach(function (state, name) {
        if (state.subscribed) {
          result.push(name);
        }
      });
      return result;
    },
    getMemberState: function getMemberState(name) {
      var state = members.get(name);
      return state ? _objectSpread({}, state) : undefined;
    },
    reset: function reset() {
      members.clear();
      visible = new Set();
    }
  };
};